'use client'

import Script from 'next/script'
import siteMetadata from '@/data/siteMetadata'

type BreadcrumbItem = { name: string; url: string }
type FAQItem = { question: string; answer: string }

interface AIOptimizedSchemaProps {
  type: 'homepage' | 'service' | 'blog'
  url?: string
  breadcrumbs?: BreadcrumbItem[]
  faqs?: FAQItem[]
  serviceData?: {
    name: string
    description: string
    price?: string
    features?: string[]
    category: string
  }
  // Optional blog data if you ever need it
  blogData?: {
    headline: string
    description?: string
    datePublished?: string // ISO
    dateModified?: string // ISO
    authorName?: string
    image?: string | string[]
  }
}

export default function AIOptimizedSchema({
  type,
  url,
  breadcrumbs = [],
  faqs = [],
  serviceData,
  blogData,
}: AIOptimizedSchemaProps) {
  const orgId = `${siteMetadata.siteUrl}/#organization`

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': orgId,
    name: 'GoDeutschland',
    alternateName: 'Die Volks-Energielösung',
    url: siteMetadata.siteUrl,
    description: siteMetadata.description,
    ...(siteMetadata.email && { email: siteMetadata.email }),
    ...(siteMetadata.phone && { telephone: siteMetadata.phone }),
    sameAs: [siteMetadata.facebook, siteMetadata.youtube, siteMetadata.instagram].filter(Boolean),
    knowsAbout: [
      'Photovoltaik',
      'Solarenergie',
      'Energiespeicher',
      'Infrarotheizung',
      'Isolierung',
      'Energieeffizienz',
      'Nachhaltigkeit',
      'Erneuerbare Energie',
      'Low Emission Coating',
      'Wallbox',
      'Energieunabhängigkeit',
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'DE',
      addressRegion: 'Deutschland',
    },
  }

  const graph: any[] = [organizationSchema]

  if (type === 'homepage') {
    graph.push(
      {
        '@type': 'WebSite',
        '@id': `${siteMetadata.siteUrl}/#website`,
        url: siteMetadata.siteUrl,
        name: siteMetadata.title,
        description: siteMetadata.description,
        publisher: { '@id': orgId },
        inLanguage: 'de-DE',
        '@context': 'https://schema.org',
      },
      {
        '@type': 'LocalBusiness',
        '@id': `${siteMetadata.siteUrl}/#localbusiness`,
        name: 'GoDeutschland',
        url: siteMetadata.siteUrl,
        description: siteMetadata.description,
        ...(siteMetadata.email && { email: siteMetadata.email }),
        ...(siteMetadata.phone && { telephone: siteMetadata.phone }),
        areaServed: { '@type': 'Country', name: 'Deutschland' },
        priceRange: '€€',
        currenciesAccepted: 'EUR',
        '@context': 'https://schema.org',
      }
    )
  }

  if (type === 'service' && serviceData) {
    const svc: any = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: serviceData.name,
      description: serviceData.description,
      provider: { '@id': orgId },
      category: serviceData.category,
      areaServed: { '@type': 'Country', name: 'Deutschland' },
    }
    if (serviceData.price) {
      svc.offers = {
        '@type': 'Offer',
        price: serviceData.price,
        priceCurrency: 'EUR',
        availability: 'https://schema.org/InStock',
        ...(url && { url }),
      }
    }
    if (serviceData.features?.length) {
      svc.additionalProperty = serviceData.features.map((feature) => ({
        '@type': 'PropertyValue',
        name: 'Feature',
        value: feature,
      }))
    }
    if (url) svc.url = url
    graph.push(svc)
  }

  if (type === 'blog' && blogData) {
    const pageUrl = url ?? siteMetadata.siteUrl
    graph.push(
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        url: pageUrl,
        name: blogData.headline,
        description: blogData.description ?? siteMetadata.description,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: blogData.headline,
        description: blogData.description ?? siteMetadata.description,
        datePublished: blogData.datePublished,
        dateModified: blogData.dateModified ?? blogData.datePublished,
        mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
        author: {
          '@type': 'Organization',
          '@id': orgId,
          name: blogData.authorName ?? 'GoDeutschland',
        },
        publisher: { '@id': orgId },
        ...(blogData.image && { image: blogData.image }),
        url: pageUrl,
      }
    )
  }

  // Optional BreadcrumbList
  if (breadcrumbs.length) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: b.url,
      })),
    })
  }

  // Optional FAQPage
  if (faqs.length) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    })
  }

  const schemaObject =
    type === 'homepage' || type === 'service' || type === 'blog'
      ? { '@context': 'https://schema.org', '@graph': graph }
      : organizationSchema

  return (
    <Script
      id={`ai-schema-${type}-${(url ?? siteMetadata.siteUrl).replace(/[^a-z0-9]/gi, '-')}`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObject) }}
    />
  )
}
